/**
 * Job Scheduler Service
 * Handles planning and execution with rate limiting
 */

import { v4 as uuidv4 } from 'uuid';
import { rateLimiter } from '../../services/shared/rateLimiter';
import type { TranslationFile } from '../../types';

// Inline token estimator (was in tokenCounter.ts, chars/token ratio for Vietnamese text)
// Guard: file metadata-only (US-19-16 lazy-load) có thể có sourceText undefined → fallback 0.
const estimateTokensFast = (text: string | undefined | null): number => {
    if (!text) return 0;
    return Math.ceil(text.length / 2.5);
};

import type {
    JobEstimate,
    ScheduledJob,
    ExecutionPlan,
    SchedulerConfig
} from './types';
import { DEFAULT_SCHEDULER_CONFIG } from './types';

// Cost per 1M tokens (gemini-2.5-flash)
const COST_PER_MILLION_INPUT = 0.075;  // $0.075 per 1M input tokens
const COST_PER_MILLION_OUTPUT = 0.30;  // $0.30 per 1M output tokens

/**
 * Estimate job details for a file
 */
export function estimateJob(file: TranslationFile): JobEstimate {
    const sourceTokens = estimateTokensFast(file.sourceText) * 2.2; // input + prompt overhead + output
    const hasHumanBenchmark = !!(file.humanBenchmark && file.humanBenchmark.length > 0);

    // Estimate tokens for each step
    const transTokens = sourceTokens;
    const matchTokens = hasHumanBenchmark ? estimateTokensFast(file.humanBenchmark!) * 2 : 0; // *2 for comparison overhead
    const auditTokens = sourceTokens * 0.5; // Audit is lighter

    // Max load at any moment is the MAX of these steps + buffer
    const maxStepTokens = Math.max(transTokens, matchTokens, auditTokens) * 1.2;

    const estimatedRequests = hasHumanBenchmark ? 3 : 2;

    return {
        fileId: file.id,
        fileName: file.fileName,
        estimatedTokens: Math.ceil(maxStepTokens),
        estimatedRequests,
        priority: file.sourceText?.length ?? 0,
        file
    };
}

/**
 * Create scheduled jobs for a file (translation + match + audit)
 */
function createJobsForFile(estimate: JobEstimate): ScheduledJob[] {
    const jobs: ScheduledJob[] = [];
    const hasHumanBenchmark = !!(estimate.file.humanBenchmark && estimate.file.humanBenchmark.length > 0);

    // Translation job
    jobs.push({
        id: uuidv4(),
        fileId: estimate.fileId,
        fileName: estimate.fileName,
        type: 'translation',
        status: 'pending',
        estimatedTokens: Math.ceil(estimate.estimatedTokens * 0.5) // Rough heuristic for step weight
    });

    // Semantic match job (if benchmark exists)
    if (hasHumanBenchmark) {
        jobs.push({
            id: uuidv4(),
            fileId: estimate.fileId,
            fileName: estimate.fileName,
            type: 'semantic-match',
            status: 'pending',
            estimatedTokens: Math.ceil(estimate.estimatedTokens * 0.5)
        });
    }

    // Audit job
    jobs.push({
        id: uuidv4(),
        fileId: estimate.fileId,
        fileName: estimate.fileName,
        type: 'audit',
        status: 'pending',
        estimatedTokens: Math.ceil(estimate.estimatedTokens * 0.3)
    });

    return jobs;
}

/**
 * Create execution plan from file list
 * Generates a flat queue of jobs
 */
export function createExecutionPlan(
    files: TranslationFile[],
    config: SchedulerConfig = DEFAULT_SCHEDULER_CONFIG
): ExecutionPlan {
    // Estimate all jobs
    const estimates = files.map(estimateJob);

    // Sort by priority (smaller files first for better throughput)
    estimates.sort((a, b) => a.priority - b.priority);

    // Create flat list of jobs
    const allJobs: ScheduledJob[] = [];
    let totalTokens = 0;

    for (const estimate of estimates) {
        const jobs = createJobsForFile(estimate);
        allJobs.push(...jobs);
        totalTokens += estimate.estimatedTokens; // Conservative sum
    }

    // Estimate time: Total jobs * avg time / concurrency
    // Simple heuristic: with 5 concurrent, time is ~1/5th
    const avgJobTimeSeconds = 10;
    const estimatedTime = (allJobs.length * avgJobTimeSeconds) / config.maxConcurrent;

    // Estimate cost (input + output tokens)
    const inputTokens = totalTokens * 0.6;
    const outputTokens = totalTokens * 0.4;
    const estimatedCost = (inputTokens / 1000000 * COST_PER_MILLION_INPUT) +
        (outputTokens / 1000000 * COST_PER_MILLION_OUTPUT);

    return {
        id: uuidv4(),
        jobs: allJobs,
        totalFiles: files.length,
        totalJobs: allJobs.length,
        estimatedTokens: totalTokens,
        estimatedTime: Math.ceil(estimatedTime),
        estimatedCost: Math.round(estimatedCost * 1000) / 1000,
        status: 'ready',
        createdAt: Date.now()
    };
}

/**
 * Create scheduled jobs for a translate file (translation + audit only, no semantic-match)
 */
function createTranslateJobsForFile(estimate: JobEstimate): ScheduledJob[] {
    const jobs: ScheduledJob[] = [];

    // Translation job
    jobs.push({
        id: uuidv4(),
        fileId: estimate.fileId,
        fileName: estimate.fileName,
        type: 'translation',
        status: 'pending',
        estimatedTokens: Math.ceil(estimate.estimatedTokens * 0.6)
    });

    // Audit job (quality check)
    jobs.push({
        id: uuidv4(),
        fileId: estimate.fileId,
        fileName: estimate.fileName,
        type: 'audit',
        status: 'pending',
        estimatedTokens: Math.ceil(estimate.estimatedTokens * 0.4)
    });

    return jobs;
}

/**
 * Create execution plan for Translate workflow (translation + quality-check only)
 * No semantic-match step since translate projects don't have human benchmark
 */
export function createTranslateExecutionPlan(
    files: TranslationFile[],
    config: SchedulerConfig = DEFAULT_SCHEDULER_CONFIG
): ExecutionPlan {
    const estimates = files.map(estimateJob);
    estimates.sort((a, b) => a.priority - b.priority);

    const allJobs: ScheduledJob[] = [];
    let totalTokens = 0;

    for (const estimate of estimates) {
        const jobs = createTranslateJobsForFile(estimate);
        allJobs.push(...jobs);
        totalTokens += estimate.estimatedTokens;
    }

    const avgJobTimeSeconds = 10;
    const estimatedTime = (allJobs.length * avgJobTimeSeconds) / config.maxConcurrent;

    const inputTokens = totalTokens * 0.6;
    const outputTokens = totalTokens * 0.4;
    const estimatedCost = (inputTokens / 1000000 * COST_PER_MILLION_INPUT) +
        (outputTokens / 1000000 * COST_PER_MILLION_OUTPUT);

    return {
        id: uuidv4(),
        jobs: allJobs,
        totalFiles: files.length,
        totalJobs: allJobs.length,
        estimatedTokens: totalTokens,
        estimatedTime: Math.ceil(estimatedTime),
        estimatedCost: Math.round(estimatedCost * 1000) / 1000,
        status: 'ready',
        createdAt: Date.now()
    };
}

/**
 * Get current rate limit capacity
 */
export function getRateLimitCapacity() {
    return rateLimiter.getCapacity();
}
